import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @attr {string} scroll-lock - Set to "overflow" to lock scrolling without fixing the body.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;
  #previousOverflow = '';

  /**
   * Returns true if the facets form's current state (checkboxes, sort, etc.)
   * would produce a different URL than the current page URL.
   * @param { { createURLParameters: () => URLSearchParams } } facetsForm
   * @returns {boolean}
   */
  #formStateDiffersFromUrl(facetsForm) {
    const formParams = facetsForm.createURLParameters();
    const currentParams = new URLSearchParams(window.location.search);
    return !this.#searchParamsEqual(formParams, currentParams);
  }

  /**
   * Compares two URLSearchParams for filter/sort relevance (keys like filter.*, sort_by, q).
   * @param {URLSearchParams} a
   * @param {URLSearchParams} b
   * @returns {boolean}
   */
  #searchParamsEqual(a, b) {
    const keysA = [...a.keys()].filter((k) => k === 'q' || k === 'sort_by' || k.startsWith('filter.'));
    const keysB = [...b.keys()].filter((k) => k === 'q' || k === 'sort_by' || k.startsWith('filter.'));
    if (keysA.length !== keysB.length) return false;
    const keySet = new Set([...keysA, ...keysB]);
    for (const key of keySet) {
      const valuesA = a.getAll(key).sort();
      const valuesB = b.getAll(key).sort();
      if (valuesA.length !== valuesB.length || valuesA.some((v, i) => v !== valuesB[i])) {
        return false;
      }
    }
    return true;
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      if (this.getAttribute('scroll-lock') === 'overflow') {
        this.#previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
      } else {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
      }

      dialog.showModal();
      const actions = dialog.querySelector('.facets__actions');
      const facetsForm = dialog.querySelector('facets-form-component');
      if (actions) {
        actions.classList.remove('facets__actions--dirty');
        // If the form has selections that differ from the current URL (e.g. user
        // selected a filter, closed drawer, then re-opened), show Apply again.
        if (facetsForm?.createURLParameters && this.#formStateDiffersFromUrl(facetsForm)) {
          actions.classList.add('facets__actions--dirty');
        }
      }
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    if (this.getAttribute('scroll-lock') === 'overflow') {
      document.documentElement.style.overflow = this.#previousOverflow;
    } else {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Applies facet filters (updates URL from form) then closes the dialog.
   * Used by the "Apply Filters" button in the filters drawer.
   */
  applyFiltersAndClose = async () => {
    const { dialog } = this.refs;
    const facetsForm = dialog?.querySelector('facets-form-component');
    if (facetsForm?.updateFilters) {
      facetsForm.updateFilters();
    }
    await this.closeDialog();
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
